import { CategoryView } from 'ish-core/models/category-view/category-view.model';
import { PriceHelper } from 'ish-core/models/price/price.helper';
import { Pricing } from 'ish-core/models/price/price.model';
import { ProductView } from 'ish-core/models/product-view/product-view.model';
import { ProductHelper } from 'ish-core/models/product/product.model';

import { GTMEventDotCmsItems, GTMEventItems } from './gtm.model';

export class GTMHelper {
  static getItem(
    item: GTMEventDotCmsItems,
    product: { product: ProductView; category: CategoryView },
    price: Pricing,
    quantity: number,
    categoryPath: string
  ): GTMEventItems {
    const dataLayerValue: GTMEventItems = {
      title: GTMHelper.getItemValue('title', item),
      contentType: GTMHelper.getItemValue('contentType', item),
      item_id: product?.product.sku,
      quantity: item?.quantity?.value || quantity,
      creative_name: GTMHelper.getItemValue('creative_name', item),
      creative_slot: GTMHelper.getItemValue('creative_slot', item),
      promotion_name: GTMHelper.getItemValue('promotion_name', item),
      promotion_id: GTMHelper.getItemValue('promotion_id', item),
      discount:
        GTMHelper.calculatePriceForProduct(product?.product, price)?.salePrice?.value -
        GTMHelper.calculatePriceForProduct(product?.product, price)?.listPrice?.value,
      item_name: product?.product?.name,
      brand: product?.product?.manufacturer,
      price:
        GTMHelper.getFormattedPrice(item?.price?.gross) ||
        GTMHelper.getFormattedPrice(GTMHelper.calculatePriceForProduct(product?.product, price)?.salePrice?.value), // Ensure price structure matches
      item_category:
        product?.product?.defaultCategory?.pathElements?.[0]?.name || product?.category?.pathElements?.[0]?.name,
      item_category2:
        product?.product?.defaultCategory?.pathElements?.[1]?.name || product?.category?.pathElements?.[1]?.name,
      item_category3:
        product?.product?.defaultCategory?.pathElements?.[2]?.name || product?.category?.pathElements?.[2]?.name,
      item_category_path: categoryPath ? categoryPath + product?.product?.name : undefined,
      item_type: GTMHelper.getProductType(product.product),
    };
    return GTMHelper.filterUndefinedValues(dataLayerValue);
  }

  static filterUndefinedValues(eventItems: GTMEventItems): GTMEventItems {
    return Object.fromEntries(
      Object.entries(eventItems)
        .filter(([_, value]) => value !== undefined) // Filter undefined values
        .map(([key, value]) =>
          // If value is an object, recursively filter its undefined values
          [
            key,
            typeof value === 'object' && !Array.isArray(value) && value !== undefined
              ? GTMHelper.filterUndefinedValues(value)
              : value,
          ]
        )
    );
  }
  static getItemValue(key: string, item: GTMEventDotCmsItems) {
    //@ts-ignore
    return item[key] ? item[key] : undefined;
  }
  static getProductType(product: ProductView): string {
    return ProductHelper.isMasterProduct(product)
      ? 'Master Product'
      : ProductHelper.isRetailSet(product)
      ? 'Retail Set'
      : ProductHelper.isProductBundle(product)
      ? 'Bundle Product'
      : ProductHelper.isVariationProduct(product)
      ? 'Variant Product'
      : 'Product';
  }
  static getFormattedPrice(value: number): string {
    return value?.toFixed(2);
  }
  static calculatePriceValue(items: GTMEventItems[]): number {
    return items.reduce((acc, cur) => (cur.price ? acc + +cur.price : acc + 0), 0);
  }
  private static calculatePriceForProduct(product: ProductView, prices: Pricing) {
    //This below logic will changed  when price range for product applied and for now only sales price is returned
    return {
      ...prices,
      isListPriceGreaterThanSalePrice: prices.listPrice?.value > prices.salePrice?.value,
      isListPriceLessThanSalePrice: prices.listPrice?.value < prices.salePrice?.value,
      priceSavings: prices.listPrice && prices.salePrice && PriceHelper.diff(prices.listPrice, prices.salePrice),
      lowerPrice: (ProductHelper.isMasterProduct(product) || ProductHelper.isRetailSet(product)) && prices.minSalePrice,
      upperPrice: ProductHelper.isMasterProduct(product)
        ? prices.maxSalePrice
        : ProductHelper.isRetailSet(product)
        ? prices.summedUpSalePrice
        : undefined,
    };
  }
}
